import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Badge,
  Box,
  Button,
  Heading,
  HStack,
  Spinner,
  Tabs,
  Text,
  VStack,
  useDisclosure,
  type TabsValueChangeDetails,
} from '@chakra-ui/react';
import { FiArrowLeft, FiEdit2 } from 'react-icons/fi'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useLobbyDayEvent, useUpdateLobbyDayEvent } from '../hooks/useLobbyDay'
import { useIsMobile } from '../hooks/useIsMobile'
import { EventFormModal } from '../components/lobbyDay/EventFormModal'
import { OutreachTab } from '../components/lobbyDay/OutreachTab'
import { AttendeesTab } from '../components/lobbyDay/AttendeesTab'
import { AsksTab } from '../components/lobbyDay/AsksTab'
import { LobbyScheduler } from '../components/lobbyDay/scheduler/LobbyScheduler'
import { useLobbyScheduleEditor } from '../hooks/useLobbyScheduleEditor'
import { LobbyScheduleAgendaEditor } from '../components/lobbyDay/scheduler/LobbyScheduleAgendaEditor'
import { DebriefTab } from '../components/lobbyDay/DebriefTab'
import { ReconciliationTab } from '../components/lobbyDay/ReconciliationTab'
import { LogisticsBanner } from '../components/lobbyDay/LogisticsBanner'
import { UnsavedChangesDialog } from '../components/lobbyDay/scheduler/UnsavedChangesDialog'
import { DayOfBoard } from '../components/lobbyDay/dayOf/DayOfBoard'
import { boardDate } from '../components/lobbyDay/dayOf/dayOfBoardModel'
import { toaster } from '@/components/ui/toaster'

// LW-5: Schedule stays the pinned hero (index 0), with the Day-of board right after it (DB-14);
// then the tabs follow the natural create→run→review flow: recruit Attendees → define Asks →
// Outreach to offices → Debrief → Reconciliation. LW-14 retired the standalone Logistics tab —
// logistics now lives in a summary banner + pull-up on the Schedule tab (LogisticsBanner).
const TAB_KEYS = ['schedule', 'dayof', 'attendees', 'asks', 'outreach', 'debrief', 'reconciliation'] as const

// Tabs that mount a schedule editor; leaving one while dirty goes through the unsaved-changes guard.
const EDITOR_TABS = new Set<string>(['schedule', 'dayof'])

function todayLocal(now: Date): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
}

export interface ScheduleEditorState {
  isDirty: boolean
  isSaving: boolean
  save: (opts?: { onSuccess?: () => void; onError?: () => void }) => void
  discardDraft: () => void
}

export function LobbyDayEventPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const editModal = useDisclosure()
  const { data: event, isLoading, error } = useLobbyDayEvent(id)
  const isMobile = useIsMobile()
  const updateEvent = useUpdateLobbyDayEvent(event?.id ?? '')

  // DB-14: with no explicit ?tab=, an event that is happening today (local) opens on the Day-of board.
  const now = new Date()
  const defaultTab = event && boardDate(event.startDate, event.endDate ?? null, now) === todayLocal(now)
    ? 'dayof' : 'schedule'
  const tabIndex = Math.max(0, TAB_KEYS.indexOf((searchParams.get('tab') ?? defaultTab) as typeof TAB_KEYS[number]))

  const [editorState, setEditorState] = useState<ScheduleEditorState>(
    { isDirty: false, isSaving: false, save: () => {}, discardDraft: () => {} })
  const handleEditorState = useCallback((s: ScheduleEditorState) => setEditorState(s), [])

  // A navigation the user attempted while dirty, held until they choose Save/Discard/Cancel.
  const [pendingNav, setPendingNavState] = useState<(() => void) | null>(null)
  // Mirrors pendingNav synchronously. A Save-and-leave click starts an async mutation whose
  // onSuccess closure captures `run` at that moment; if the user cancels (or hits Escape /
  // backdrop) before it resolves, pendingNav is cleared but that closure still holds the stale
  // `run`. Reading pendingNavRef.current inside onSuccess (a ref, not the state closure) lets it
  // see the cancellation and skip navigating, instead of firing a navigation the user just refused.
  const pendingNavRef = useRef<(() => void) | null>(null)
  const setPendingNav = useCallback((run: (() => void) | null) => {
    pendingNavRef.current = run
    // setPendingNavState is the raw useState dispatch: a bare function argument is interpreted
    // as an updater and invoked immediately, so `run` itself must be wrapped -- otherwise React
    // calls `run` (firing the navigation right now) instead of storing it as the next state.
    setPendingNavState(() => run)
  }, [])

  const guardedNav = useCallback((run: () => void) => {
    if (editorState.isDirty) { setPendingNav(run); return }
    run()
  }, [editorState.isDirty, setPendingNav])

  const resolvePending = useCallback((mode: 'save' | 'discard' | 'cancel') => {
    const run = pendingNav
    if (mode === 'cancel') { setPendingNav(null); return }
    if (mode === 'discard') { editorState.discardDraft(); setPendingNav(null); run?.(); return }
    editorState.save({
      onSuccess: () => {
        // The save persisted either way. Only navigate if nothing has cancelled this pending
        // navigation since the save started -- otherwise the user explicitly chose to stay.
        if (pendingNavRef.current !== run) return
        setPendingNav(null)
        run?.()
      },
      onError: () => {
        toaster.create({ title: 'Failed to save schedule', type: 'error', duration: 3000 })
      },
    })
  }, [pendingNav, editorState, setPendingNav])

  if (isLoading) return <VStack py={12}><Spinner size="lg" color="accent" /></VStack>
  if (!!error || !event) return <Text color="red.500">Event not found.</Text>

  const publish = () => updateEvent.mutate({
    name: event.name, location: event.location ?? null, description: event.description ?? null,
    startDate: event.startDate, endDate: event.endDate ?? null, status: 'Active',
  })

  return (
    <Box>
      <HStack mb={4} justify="space-between">
        <HStack>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => guardedNav(() => navigate('/dashboard/lobby-day'))}><FiArrowLeft />Events
                      </Button>
          <Heading size="lg">{event.name}</Heading>
          <Badge colorPalette={event.status === 'Active' ? 'green' : 'gray'}>{event.status}</Badge>
          {event.status === 'Draft' && (
            <Button size="sm" colorPalette="brand" onClick={publish} loading={updateEvent.isPending}>
              Publish — turn on attendee links
            </Button>
          )}
        </HStack>
        <Button size="sm" onClick={editModal.onOpen}><FiEdit2 />Edit</Button>
      </HStack>
      <Text color="gray.600" mb={4}>
        {event.startDate}{event.endDate ? ` — ${event.endDate}` : ''}{event.location ? ` · ${event.location}` : ''}
      </Text>

      <Tabs.Root
        value={TAB_KEYS[tabIndex]}
        onValueChange={(details: TabsValueChangeDetails) => {
          const current = TAB_KEYS[tabIndex]
          const go = () => setSearchParams({ tab: details.value })
          if (EDITOR_TABS.has(current) && details.value !== current) { guardedNav(go); return }
          go()
        }}
        colorPalette="brand" lazyMount unmountOnExit
      >
        <Tabs.List overflowX="auto" overflowY="hidden"
          css={{
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
            '& > button': { flexShrink: 0 }
          }}>
          <Tabs.Trigger value="schedule">Schedule</Tabs.Trigger>
          <Tabs.Trigger value="dayof">Day-of</Tabs.Trigger>
          <Tabs.Trigger value="attendees">Attendees ({event.attendeeCount})</Tabs.Trigger>
          <Tabs.Trigger value="asks">Asks &amp; Packets ({event.asks.length})</Tabs.Trigger>
          <Tabs.Trigger value="outreach">Outreach</Tabs.Trigger>
          <Tabs.Trigger value="debrief">Debrief</Tabs.Trigger>
          <Tabs.Trigger value="reconciliation">Reconciliation</Tabs.Trigger>
        </Tabs.List>
        <Tabs.ContentGroup>
          <Tabs.Content value="schedule" px={0}>
            <LogisticsBanner eventId={event.id} />
            {isMobile
              ? <MobileScheduleTab eventId={event.id} onEditorState={handleEditorState} />
              : <LobbyScheduler eventId={event.id} onEditorState={handleEditorState} />}
          </Tabs.Content>
          <Tabs.Content value="dayof" px={0}><DayOfBoard eventId={event.id} onEditorState={handleEditorState} /></Tabs.Content>
          <Tabs.Content value="attendees" px={0}><AttendeesTab eventId={event.id} eventStatus={event.status} /></Tabs.Content>
          <Tabs.Content value="asks" px={0}><AsksTab event={event} /></Tabs.Content>
          <Tabs.Content value="outreach" px={0}><OutreachTab eventId={event.id} /></Tabs.Content>
          <Tabs.Content value="debrief" px={0}><DebriefTab eventId={event.id} /></Tabs.Content>
          <Tabs.Content value="reconciliation" px={0}><ReconciliationTab eventId={event.id} /></Tabs.Content>
        </Tabs.ContentGroup>
      </Tabs.Root>

      <EventFormModal isOpen={editModal.open} onClose={editModal.onClose} event={event} />

      <UnsavedChangesDialog
        isOpen={pendingNav !== null}
        isSaving={editorState.isSaving}
        onSave={() => resolvePending('save')}
        onDiscard={() => resolvePending('discard')}
        onCancel={() => resolvePending('cancel')}
      />
    </Box>
  );
}

function MobileScheduleTab({ eventId, onEditorState }: {
  eventId: string; onEditorState: (s: ScheduleEditorState) => void
}) {
  const editor = useLobbyScheduleEditor(eventId)
  const { isDirty, isSaving, save, discardDraft } = editor
  useEffect(() => {
    onEditorState({ isDirty, isSaving, save, discardDraft })
  }, [isDirty, isSaving, save, discardDraft, onEditorState])
  useEffect(() => () => onEditorState({ isDirty: false, isSaving: false, save: () => {}, discardDraft: () => {} }), [onEditorState])
  return <LobbyScheduleAgendaEditor editor={editor} />
}
